import React, { Component } from 'react';
import actions from 'actions';
import Loading from 'components/Loading';
import LoadingPage from 'components/LoadingPage';
import Button from 'UI/Button';
import './styles.scss';

const PENDINGS_QUANTITY = 50;
const PROGRESS_LETTERS = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';

const getQueueTelemetryProperties = metadata => {
  const count = metadata?.count;
  const ageSampleCount = metadata?.ageSampleCount;
  const oldestItemAt = metadata?.oldestItemAt;
  const numericCount = count === null || typeof count === 'undefined' ? null : Number(count);
  const numericAgeSampleCount =
    ageSampleCount === null || typeof ageSampleCount === 'undefined'
      ? null
      : Number(ageSampleCount);
  const numericOldestItemAt =
    oldestItemAt === null || typeof oldestItemAt === 'undefined'
      ? null
      : Number(oldestItemAt);
  const now = Date.now();

  return {
    ...(Number.isInteger(numericCount) && numericCount >= 0
      ? { queue_count: numericCount }
      : {}),
    ...(Number.isInteger(numericAgeSampleCount) && numericAgeSampleCount >= 0
      ? { queue_age_sample_count: numericAgeSampleCount }
      : {}),
    ...(Number.isFinite(numericOldestItemAt) &&
    numericOldestItemAt > 0 &&
    numericOldestItemAt <= now
      ? { oldest_item_age_seconds: Math.round((now - numericOldestItemAt) / 1000) }
      : {}),
  };
};

class Curator extends Component {
  telemetry = null;
  photoShownAt = new Map();
  decisionAttempts = new Map();

  state = {
    i: 0,
    pendings: [],
    isPreloaded: false,
    isLoading: true,
    showButtons: true,
    noMorePhotos: false,

    count: 0,
    startedAt: 0,
  };

  componentDidMount() {
    this.telemetry = actions.moderationTelemetry.startSession(
      this.props.action === 'preCuration' ? 'precuration' : 'curation'
    );
    this.getPendings();
    document.addEventListener('keydown', this.onKeyPress);
  }

  componentWillUnmount() {
    this.telemetry?.stop('unmount');
    document.removeEventListener('keydown', this.onKeyPress);
  }

  onKeyPress = event => {
    if (event.key === 'ArrowLeft') {
      this._onReject();
      this.setState({ showButtons: false });
    } else if (event.key === 'ArrowRight') {
      this._onApprove();
      this.setState({ showButtons: false });
    } else if (event.key === 'Escape') {
      this.showPrev();
    }
  };

  showPrev = () => {
    if (this.state.i > 0) {
      this.setState({
        i: this.state.i - 1,
        count: this.state.count - 1,
      });
    }
  };

  getPendings = () => {
    const loadStartedAt = Date.now();
    const queue = this.props.action === 'preCuration' ? 'precuration' : 'curation';

    actions.photos[this.props.action]
      .getPendings(PENDINGS_QUANTITY)
      .then(({ photos: pendings, queue: queueMetadata }) => {
        this.telemetry?.track('MODERATION_QUEUE_LOADED', {
          queue,
          stage: 'queue',
          load_succeeded: true,
          load_duration_ms: Date.now() - loadStartedAt,
          ...getQueueTelemetryProperties(queueMetadata),
        });
        this.setState({
          pendings,
          i: 0,
          isPreloaded: false,
          noMorePhotos: pendings.length === 0,
        });
      })
      .catch(error => {
        this.telemetry?.track('MODERATION_QUEUE_LOAD_FAILED', {
          queue,
          stage: 'queue',
          load_succeeded: false,
          load_duration_ms: Date.now() - loadStartedAt,
          error_type: actions.moderationTelemetry.classifyError(error),
        });
        throw error;
      });
  };

  preloadPhotos = () => {
    this.state.pendings.forEach(photo => {
      new Image().src = photo.urls.regular;
    });
  };

  checkGetMorePhotos = () => {
    if (this.state.pendings.length - this.state.i === 0 && !this.state.isLoading) {
      this.setState({ isLoading: true });
      // TODO: Remove this 500.
      // Instead, check if the last promises were solved
      setTimeout(this.getPendings, 500);
    }
  };

  _onImageFinishLoad = () => {
    const photo = this.state.pendings[this.state.i];

    if (photo) {
      this.photoShownAt.set(photo.id, Date.now());
    }

    this.telemetry?.track('MODERATION_PHOTO_SHOWN', {
      queue: this.props.action === 'preCuration' ? 'precuration' : 'curation',
      stage: 'review',
      ai_evidence_available: false,
      nearby_evidence_evaluated: false,
      nearby_evidence_available: false,
    });
    this.setState({ isLoading: false });

    if (!this.state.isPreloaded) {
      this.preloadPhotos();
      this.setState({ isPreloaded: true });
    }
  };

  _onApprove = () => {
    this._decide('approve');
  };

  _onReject = () => {
    this._decide('reject');
  };

  _decide = decisionType => {
    const photo = this.state.pendings[this.state.i];

    if (!photo) {
      return;
    }

    const queue = this.props.action === 'preCuration' ? 'precuration' : 'curation';
    const attemptNumber = Number(this.decisionAttempts.get(photo.id) || 0) + 1;
    const properties = {
      decision_id: actions.moderationTelemetry.createId(),
      queue,
      stage: 'review',
      decision_type: decisionType,
      is_final_decision: false,
      decision_duration_seconds: Number(
        ((Date.now() - (this.photoShownAt.get(photo.id) || Date.now())) / 1000).toFixed(1)
      ),
      attempt_number: attemptNumber,
      retry_count: attemptNumber - 1,
      is_retry: attemptNumber > 1,
      ai_evidence_available: false,
      nearby_evidence_evaluated: false,
      nearby_evidence_available: false,
    };
    const requestStartedAt = Date.now();

    this.decisionAttempts.set(photo.id, attemptNumber);
    this.telemetry?.track('MODERATION_DECISION_ATTEMPTED', properties);

    actions.photos[this.props.action][decisionType](photo.id)
      .then(() => {
        this.decisionAttempts.delete(photo.id);
        this.telemetry?.track('MODERATION_DECISION_SUCCEEDED', {
          ...properties,
          request_duration_ms: Date.now() - requestStartedAt,
        });
      })
      .catch(error => {
        this.telemetry?.track('MODERATION_DECISION_FAILED', {
          ...properties,
          request_duration_ms: Date.now() - requestStartedAt,
          error_type: actions.moderationTelemetry.classifyError(error),
        });
      });

    this._nextPhoto();
  };

  _nextPhoto = () => {
    const newState = {
      i: this.state.i + 1,
      count: this.state.count + 1,
    };

    if (this.state.startedAt === 0) {
      newState.startedAt = Date.now();
    }

    this.setState(newState, this.checkGetMorePhotos);
  };

  render() {
    const timeTake = (Date.now() - this.state.startedAt) / 1000 / 60;

    if (this.state.noMorePhotos) {
      return <h1>No more photos</h1>;
    }

    const photo = this.state.pendings[this.state.i] || { urls: {} };

    let progress = 0;

    if (photo.id) {
      progress = (PROGRESS_LETTERS.indexOf(photo.id[0]) / PROGRESS_LETTERS.length) * 100;
      progress += PROGRESS_LETTERS.indexOf(photo.id[1]) / PROGRESS_LETTERS.length;
    }

    return (
      <div className="Curator content">
        {this.state.isLoading && <LoadingPage />}

        {this.state.count > 0 && (
          <div className="data">
            {this.state.count} photos curated in {Math.round(timeTake)}min (
            {Math.round((60 * this.state.count) / timeTake)}/h). Progress: {progress.toFixed(2)}%
          </div>
        )}

        <div className={`current ` + (!this.state.showButtons ? '--with-no-buttons' : '')}>
          {this.state.pendings.length - this.state.i === 0 ? (
            <Loading />
          ) : (
            <img
              key={this.state.i}
              onLoad={this._onImageFinishLoad}
              src={photo.urls.regular}
              alt="Current"
              data-id={photo.id}
            />
          )}

          {this.state.showButtons && (
            <div className="buttons">
              <Button className="reject" onClick={this._onReject}>
                Reject
              </Button>
              <Button className="approve" onClick={this._onApprove}>
                Approve
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Curator;
